<template>
  <div class="payment-datatable p-4">
    <h2 class="mb-4">
      <CreditCard :size="24" class="me-2" />
      Payment Checkouts
    </h2>

    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          <strong>All Payment Links</strong>
          <small class="text-muted ms-2">Manage Square checkout pages</small>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-sm btn-outline-secondary" @click="loadPayments">
            <RefreshCw :size="16" class="me-1" />
            Refresh
          </button>
        </div>
      </div>
      <div class="card-body p-0">
        <div v-if="loading" class="text-center py-5">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading payments...
        </div>
        <div v-else-if="payments.length === 0" class="text-center py-5 text-muted">
          <CreditCard :size="48" class="mb-3 opacity-50" />
          <p>No payment checkouts created yet</p>
          <small>Payment links are automatically generated when invoices are viewed</small>
        </div>
        <div v-else class="table-responsive">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in payments" :key="payment.Id">
                <td>
                  <router-link :to="`/workorder/edit/${payment.WorkOrderId}`">
                    {{ payment.InvoiceNumber }}
                  </router-link>
                </td>
                <td>{{ payment.CustomerName || 'N/A' }}</td>
                <td>{{ formatCurrency(payment.Amount) }}</td>
                <td>
                  <span class="badge" :class="getStatusBadgeClass(payment.Status)">
                    {{ payment.Status }}
                  </span>
                </td>
                <td>{{ formatDate(payment.CreatedDate) }}</td>
                <td>{{ formatDate(payment.UpdatedDate) }}</td>
                <td>
                  <div class="btn-group btn-group-sm">
                    <button 
                      class="btn btn-outline-primary"
                      @click="viewPaymentLink(payment)"
                      title="View Payment Link"
                    >
                      <ExternalLink :size="14" />
                    </button>
                    <button 
                      class="btn btn-outline-secondary"
                      @click="copyPaymentLink(payment)"
                      title="Copy Link"
                    >
                      <Copy :size="14" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { CreditCard, RefreshCw, ExternalLink, Copy } from 'lucide-vue-next';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';
import { useFormatting } from '@/composables';

const { formatCurrency, formatDate } = useFormatting();

const loading = ref(true);
const payments = ref<any[]>([]);

const loadPayments = async () => {
  try {
    loading.value = true;
    payments.value = await ApiService.getPaymentCheckouts();
  } catch (error) {
    console.error('Error loading payments:', error);
    Alert.notify({ text: 'Failed to load payments', type: 'error' });
  } finally {
    loading.value = false;
  }
};

const getStatusBadgeClass = (status: string) => {
  const statusMap: Record<string, string> = {
    'Paid': 'bg-success',
    'Pending': 'bg-warning',
    'Failed': 'bg-danger',
    'Cancelled': 'bg-secondary',
    'Expired': 'bg-dark'
  };
  return statusMap[status] || 'bg-secondary';
};

const viewPaymentLink = (payment: any) => {
  const url = `${window.location.origin}/#/invoice/${payment.WorkOrderId}/${payment.SecureHash}`;
  window.open(url, '_blank');
};

const copyPaymentLink = (payment: any) => {
  const url = `${window.location.origin}/#/invoice/${payment.WorkOrderId}/${payment.SecureHash}`;
  navigator.clipboard.writeText(url);
  Alert.notify({ text: 'Payment link copied to clipboard', type: 'success' });
};

onMounted(() => {
  loadPayments();
});
</script>
